"use client"

import React, { useEffect, useMemo, useState, memo } from "react";
import Advertisement from "@/components/Advertisement";
import Newsletter from "@/components/Newsletter";
import CategoryUnderline from "@/components/ui/CategoryUnderline";
import Link from "next/link";
import axiosInstance from "@/lib/axios";
import { GRID_COUNTS, take } from "@/lib/gridCounts";

const SIDEBAR_TRENDING_COUNT = 4;

function sidebarTrendingFromData(data) {
    const fromTrending = take(data?.trending_news, SIDEBAR_TRENDING_COUNT)
    if (fromTrending.length > 0) return fromTrending
    return take(data?.latest_news, SIDEBAR_TRENDING_COUNT)
}

const MainPageSidebar = ({ data = null }) => {
    const [puneUpdates, setPuneUpdates] = useState(() =>
        take(data?.pune_updates || [], GRID_COUNTS.SUBCITIES)
    );
    const [trendingItems, setTrendingItems] = useState(() => sidebarTrendingFromData(data));

    const pageTrending = useMemo(() => sidebarTrendingFromData(data), [data])

    const getTrendingNews = async () => {
        if (pageTrending.length > 0) {
            setTrendingItems(pageTrending)
            return
        }

        try {
            const response = await axiosInstance.get('/news/trending', {
                params: { page: 1, limit: SIDEBAR_TRENDING_COUNT },
            })
            if (response.data.success) {
                setTrendingItems(take(response.data.data, SIDEBAR_TRENDING_COUNT))
            }
        } catch (error) {
            console.error('Error fetching trending news:', error)
        }
    }

    const getPuneUpdates = async () => {
        try {
            const response = await axiosInstance.get('/specific-city-wise-news', {
                params: { district: 'Pune', limit: 3 }
            });

            if (response.data.success) {
                setPuneUpdates(response.data.data.news || []);
            }
        } catch (error) {
            console.error('Error fetching Pune updates:', error);
        }
    }

    useEffect(() => {
        getTrendingNews();
    }, [pageTrending]);

    useEffect(() => {
        if (data?.pune_updates?.length) {
            setPuneUpdates(take(data.pune_updates, GRID_COUNTS.SUBCITIES))
            return
        }
        getPuneUpdates();
    }, [data]);

    return (
        <aside className="space-y-6">
            <Advertisement
                page="home"
                position="top"
                className="mx-auto rounded-lg overflow-hidden"
            />

            <div className="sidebar-card">
                <CategoryUnderline
                    name="Maharashtra"
                    label="ट्रेंडिंग"
                    url="/trending"
                />
                <ul className="list-none m-0 p-0 flex flex-col gap-3">
                    {trendingItems.map((item, i) => (
                        <li key={item?.id || i} className="border-b border-(--border-default) last:border-0 pb-3 last:pb-0">
                            <Link
                                href={`/news/${item?.slug}`}
                                className="mr text-sm font-semibold leading-snug text-(--text-primary) hover:text-(--brand-primary) transition-colors line-clamp-2"
                            >
                                {item?.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            <Advertisement
                page="home"
                position="middle"
                className="mx-auto rounded-lg overflow-hidden"
            />

            <Newsletter />

            <div className="sidebar-card">
                <CategoryUnderline name="Pune" label="पुण्यातील अद्यतन" url="/city/pune" />
                <ul className="list-none m-0 p-0 flex flex-col gap-3">
                    {take(puneUpdates, GRID_COUNTS.SUBCITIES).map((item, i) => (
                        <li key={i} className="border-b border-(--border-default) last:border-0 pb-3 last:pb-0">
                            <Link
                                href={`/news/${item?.slug}`}
                                className="mr text-sm font-semibold leading-snug text-(--text-primary) hover:text-(--brand-primary) transition-colors line-clamp-2"
                            >
                                {item?.title || item}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </aside>
    );
};

export default memo(MainPageSidebar);
